import { Component } from '@angular/core';
import { AuthService, BreadcrumbService, IProductNameConfiguration, ProductNameService, StateService, Utils } from 'src/app/services';
import { Company } from 'identity-webapi-client';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { 
  ListRenderedOutputsRequest, ListRenderedOutputsResponse, ProductName, RenderedOutputStatus, RenderedOutputSummary, 
  SituViewWebApiClient 
} from 'situview-webapi-client';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { ViewControls } from 'src/app/shared/models/view-controls';
import { PageEvent } from '@angular/material/paginator';

@Component({
  selector: 'app-cached-homes',
  templateUrl: './cached-homes.component.html',
  styleUrls: ['./cached-homes.component.scss']
})
export class CachedHomesComponent extends SituBaseComponent {
  selectedProduct: IProductNameConfiguration | undefined;
  products: IProductNameConfiguration[] | undefined;
  viewControls = new ViewControls(25, 0, 0, [25, 50, 100, 1000]);

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  productName = ProductName;
  status = RenderedOutputStatus;

  isProductionOnly: boolean = false;
  previousSearchTerm = "";
  searchTerm = "";
  dataSource = new MatTableDataSource<RenderedOutputSummary>();
  
  constructor(
    authService: AuthService,
    stateService: StateService,
    private breadcrumbService: BreadcrumbService,
    private situViewClient: SituViewWebApiClient,
    private productNameService: ProductNameService,
    private router: Router,
    private snackBar: MatSnackBar) {
      super(authService, stateService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.products = this.productNameService.productNameConfigurations;
    this.selectedProduct = this.productNameService.productNameConfiguration;
    
    this._subscriptions.push(this.stateService.state$.subscribe((state) => {
      let company = state.company as Company ?? undefined
      if (this.selectedCompany?.id !== company.id) {
        this.selectedCompany = state.company as Company ?? undefined;
      }
    }));
  }

  protected onSelectedCompanyChange(): void {
    this.loadRenderedOutputs();
  }

  productChange(product: any) {
    if (product === undefined) return;
    this.selectedProduct = product.value;
    this.productNameService.productNameConfiguration = product.value;
    this.loadRenderedOutputs();
  }

  updateParams() {
    const urlTree = this.router.createUrlTree([], {
      queryParamsHandling: "merge",
      preserveFragment: true
    });

    urlTree.queryParams = this.isProductionOnly ? { liveOnly: 'true' } : {};

    this.router.navigateByUrl(urlTree);
  }

  onClickExport() {
    this.loading = true;
    let request = new ListRenderedOutputsRequest();
    request.companyId = this.selectedCompany?.id;
    request.pageNumber = 1;
    request.pageSize = 10000;
    request.productName = this.selectedProduct?.productName ?? ProductName.Panorama;
    request.isProduction = true;
    request.orderByHome = true;
    if (this.searchTerm) request.containsWords = [this.searchTerm];

    this.situViewClient.listRenderedOutputs(request)
      .then((response: ListRenderedOutputsResponse) => {
        if (response.success && response?.renderedOutputs) {
          let rows: any[][] = [['Builder', 'Home', 'Master File', 'Options', 'Style Template']];
          response.renderedOutputs.forEach((x: RenderedOutputSummary) => {
            rows.push([x.homeConfig?.company ?? '', x.homeConfig?.home ?? '', x.homeConfig?.masterFile ?? '', x.homeConfig?.options?.join(', ') ?? '', x.styleConfigTemplate?.name ?? '']);
          });
          
          Utils.exportToCsv(`${ProductName[this.selectedProduct!.productName!].toLowerCase()}-cached-homes.csv`, rows);
        }
      })
      .catch((x: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your cached renders. Please try again.");
      })
      .finally(() => {
        this.loading = false;
      });
    
  }

  loadRenderedOutputs() {
    this.loading = true;
    let request = new ListRenderedOutputsRequest();
    request.companyId = this.selectedCompany?.id;
    request.pageNumber = this.viewControls.pageIndex + 1;
    request.pageSize = this.viewControls.pageSize;
    request.productName = this.selectedProduct?.productName ?? ProductName.Panorama;
    request.isProduction = true;
    request.orderByHome = true;
    if (this.searchTerm) request.containsWords = [this.searchTerm];

    this.situViewClient.listRenderedOutputs(request)
      .then((response: ListRenderedOutputsResponse) => {
        if (response.success && response?.renderedOutputs) {
          this.dataSource.data = response.renderedOutputs;
          this.viewControls.totalItems = response.totalItems ?? 0;
        }
      })
      .catch((x: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your cached renders. Please try again.");
      })
      .finally(() => {
        this.loading = false;
      });
  }

  getProductColour(productName: ProductName) {
    return this.productNameService.getProductNameConfiguration(productName as any)?.productColor;
  }

  getStatusIconAndClass(status: RenderedOutputStatus) {
    switch (status) {
      case RenderedOutputStatus.Complete:
        return { icon: "check_circle", class: "text-success", description: "Completed" };
      case RenderedOutputStatus.InProgress:
        return { icon: "pending", class: "text-info", description: "In Progress" };
      case RenderedOutputStatus.Queued:
        return { icon: "pending", class: "text-muted", description: "Queued" };
      case RenderedOutputStatus.Failed:
        return { icon: "error", class: "text-danger", description: "Failed" };
      case RenderedOutputStatus.Cancelled:
        return { icon: "cancel", class: "text-muted", description: "Cancelled" };
    }
  }

  onPageChange(event: PageEvent) {
    this.viewControls.pageSize = event.pageSize;
    this.viewControls.pageIndex = event.pageIndex;

    this.loadRenderedOutputs();
  }

  resetSearch() {
    this.viewControls.pageIndex = 0;
    this.viewControls.pageSize = 25;
    this.viewControls.totalItems = 0;
  }

  isOlderThan2Minutes(date: Date) {
    const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000); // 2 minutes in milliseconds
    return date < twoMinutesAgo;
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}
